import { z } from 'zod';

/**
 * Dashboard build-time public configuration.
 *
 * Only `PUBLIC_*` values may appear here. The Paddle **client** token and environment are public
 * by design; the Paddle API key, webhook secret, and price mapping are Platform API secrets and
 * must never be referenced from browser code.
 */
/**
 * An empty value means "not configured".
 *
 * The image build passes these through Docker `ARG`s, which arrive as empty strings when the
 * variable is not set on the service. Validating `''` against the environment enum would stop the
 * dashboard loading at all, where the intent is only that billing is unavailable.
 */
function unsetWhenEmpty<T extends z.ZodType>(schema: T) {
  return z.preprocess((value) => (value === '' ? undefined : value), schema);
}

export const dashboardEnvSchema = z.object({
  PUBLIC_APP_URL: z.url().default('http://localhost:5173'),
  PUBLIC_API_URL: z.string().default('/api'),
  PUBLIC_SITE_URL: z.url().default('http://localhost:4321'),
  PUBLIC_PADDLE_ENVIRONMENT: unsetWhenEmpty(z.enum(['sandbox', 'production']).optional()),
  PUBLIC_PADDLE_CLIENT_TOKEN: unsetWhenEmpty(z.string().optional()),
});

export type DashboardEnv = z.infer<typeof dashboardEnvSchema>;

function readEnv(): DashboardEnv {
  const result = dashboardEnvSchema.safeParse(import.meta.env);
  if (result.success) return result.data;

  const details = result.error.issues
    .map((issue) => `  - ${issue.path.join('.') || '(root)'}: ${issue.message}`)
    .join('\n');
  throw new Error(`Invalid dashboard configuration:\n${details}`);
}

export const env: DashboardEnv = readEnv();

/** Billing controls stay disabled until both public Paddle values are configured. */
export const isBillingConfigured =
  Boolean(env.PUBLIC_PADDLE_CLIENT_TOKEN) && Boolean(env.PUBLIC_PADDLE_ENVIRONMENT);
